import { Button } from "@dropbox/dig-components/buttons";
import { Text } from "@dropbox/dig-components/typography";
import { Box } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import { DownloadLine } from "@dropbox/dig-icons/dist/mjs/assets";
import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { PAP_Continue_DashSelfServeOnboarding } from "@mirage/analytics/events/types/continue_dash_self_serve_onboarding";
import { PAP_Select_DashSelfServeOnboardingStepSkip } from "@mirage/analytics/events/types/select_dash_self_serve_onboarding_step_skip";
import { useUpdateWithCheckResult } from "@mirage/eligibility/useEligibilityCheck";
import useDropboxAccount from "@mirage/service-auth/useDropboxAccount";
import { markDashUserDfbOnboardingCompleteIfNecessary } from "@mirage/service-onboarding";
import { openURL } from "@mirage/service-platform-actions";
import { canProvisionFreeDashTeam } from "@mirage/service-provisioning";
import { fetchUserEligibility } from "@mirage/shared/eligibility";
import { DESKTOP_DOWNLOAD_URL } from "@mirage/shared/urls";
import i18n from "@mirage/translations";
import { RoutePath } from "@mirage/webapp/routeTypes";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useFinishOnboarding } from "../flows/utils";
import { OnboardingResponsesProvider } from "../hooks/OnboardingResponsesProvider";
import { useProvisioning } from "../hooks/useProvisioning";
import { useSelfServeOnboardingState } from "../hooks/useSelfServeOnboardingState";
import styles from "./SelfServeOnboarding.module.css";
import { SelfServeOnboardingItem } from "./SelfServeOnboardingItem";
import { SelfServeOnboardingLayout } from "./SelfServeOnboardingLayout";
import { SelfServeOnboardingStep } from "./types";

import type { OnboardingStep } from "@mirage/analytics/events/enums/onboarding_step";
import { PAP_Click_DashOnboardingDownloadDesktopAppButton } from "@mirage/analytics/events/types/click_dash_onboarding_download_desktop_app_button";
import type { ONBOARDING_VERSION } from "../flows/constants";

export enum ActionButtonType {
  Continue = "continue",
  Skip = "skip",
  Later = "later",
  Download = "download",
}

export type OnboardingStepItem = {
  step: SelfServeOnboardingStep;
  title: string;
  description?: JSX.Element | string;
  secondaryAction?: ActionButtonType;
  primaryActions: ActionButtonType[];
  skipValidation?: boolean;
  hidden?: boolean;
};

type SelfServeOnboardingProps = {
  canProvisionDashFreemiumTeam: boolean;
  isAdmin: boolean;
  routeParams?: Record<string, string | undefined>;
  steps?: OnboardingStepItem[];
  onboardingVariant: ONBOARDING_VERSION;
  isDevboxBackend?: boolean;
  onStepChange?: (step: SelfServeOnboardingStep) => void;
};

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const fetchUserEligibilityWithRetry = async (
  maxRetries = 10,
  delayMs = 500,
) => {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    const result = await fetchUserEligibility();
    if (result.dash_user_attributes?.is_eligible) {
      return result;
    }

    if (attempt < maxRetries - 1) {
      await delay(delayMs);
    }
  }

  // Final attempt
  return await fetchUserEligibility();
};

export const SelfServeOnboarding = ({
  isAdmin,
  canProvisionDashFreemiumTeam,
  steps,
  routeParams,
  onboardingVariant,
  isDevboxBackend = false,
  onStepChange,
}: SelfServeOnboardingProps) => {
  const [currentStep, setCurrentStep] = useState<
    OnboardingStepItem | undefined
  >(undefined);
  const { provisionFreeDashTeam } = useProvisioning();
  const [currentStepIndex, setCurrentStepIndex] = useState<number>(0);
  const [continueDisabled, setContinueDisabled] = useState<boolean>(true);
  const [continueLoading, setContinueLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const { reportPapEvent } = useMirageAnalyticsContext();
  const { search } = useLocation();
  const params = useMemo(() => new URLSearchParams(search), [search]);
  const fromDesktop = params.get("from") === "desktop";
  // secret url param to use to test against the dev desktop app
  const useDevDesktopApp = params.get("useDevDesktopApp") === "true";
  const finishOnboarding = useFinishOnboarding(
    reportPapEvent,
    onboardingVariant,
    true,
  );
  const publicAccountId = useDropboxAccount()?.public_account_id;
  const updateWithCheckResult = useUpdateWithCheckResult(
    publicAccountId,
    isDevboxBackend,
  );
  const selfServeOnboardingState = useSelfServeOnboardingState();
  const isAdminFlow = canProvisionDashFreemiumTeam || isAdmin;

  const onboardingStepRef = useRef();

  useEffect(() => {
    if (!routeParams || !steps) return;
    const { step } = routeParams;
    const index = steps.findIndex((s) => s.step === step);
    if (index >= 0) {
      setCurrentStepIndex(index);
      setCurrentStep(steps[index]);
    }
  }, [routeParams, search, steps]);

  useEffect(() => {
    if (!steps) return;
    const currentStep = steps[currentStepIndex];
    if (!currentStep) return;
    setCurrentStep(steps[currentStepIndex]);
    if (onStepChange) onStepChange(steps[currentStepIndex].step);
  }, [currentStepIndex, onStepChange, steps]);

  const getNextStepIndex = () => {
    if (!steps) return undefined;
    let nextStepIndex = currentStepIndex + 1;
    while (nextStepIndex < steps.length && steps[nextStepIndex].hidden) {
      nextStepIndex++;
    }
    const isAtTheEnd = nextStepIndex >= steps.length;
    if (isAtTheEnd) return undefined;
    return nextStepIndex;
  };

  const getNextStep = () => {
    const nextStepIndex = getNextStepIndex();
    if (!nextStepIndex || !steps) return undefined;
    return steps[nextStepIndex];
  };

  const handleContinue = async (skipValidation = false) => {
    if (!steps) return;
    setContinueLoading(true);
    let success = true;
    if (!skipValidation && onboardingStepRef.current) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      success = await (onboardingStepRef.current as any).submitSurvey();
    }

    setContinueLoading(false);

    // Continue when success is true or undefined
    if (success !== false) {
      const nextStepIndex = getNextStepIndex();

      if (!nextStepIndex) {
        await finishOnboarding(fromDesktop, useDevDesktopApp);
        return;
      }

      const currentStep = steps[currentStepIndex];
      const nextStep = steps[nextStepIndex];
      // For DFB onboarding, we need to load webapp from scratch again so that `useIsDfbUser` returns updated value `true`
      // Otherwise, homepage will render DFI version of page. Since connectors step is right after the step that provisions dash,
      // we are loading connectors step as full page load for admins. Technically, we just need to do this for the first admin, but
      // that information is tricky to funnel it here, so we are doing it for all admins.
      const requireFullPageLoad =
        isAdminFlow &&
        currentStep.step === SelfServeOnboardingStep.JTBD &&
        nextStep.step === SelfServeOnboardingStep.CONNECTORS;
      if (requireFullPageLoad) {
        window.location.href = getNavigatePathForStep(nextStep.step);
      } else {
        navigate(getNavigatePathForStep(nextStep.step));
        setCurrentStepIndex(nextStepIndex);
      }
    }
  };

  const onOnboardingFinished = useCallback(async () => {
    // TODO: Change how we route on finish based on the JTBD response
    await finishOnboarding(fromDesktop, useDevDesktopApp);
  }, [finishOnboarding, fromDesktop, useDevDesktopApp]);

  const handleDownload = useCallback(() => {
    onOnboardingFinished();
    reportPapEvent(
      PAP_Click_DashOnboardingDownloadDesktopAppButton({
        featureLine: "onboarding",
      }),
    );
    openURL(DESKTOP_DOWNLOAD_URL);
  }, [onOnboardingFinished, reportPapEvent]);

  const logOnboardingStepSkip = (step: OnboardingStep | null) => {
    if (!step) return;
    reportPapEvent(
      PAP_Select_DashSelfServeOnboardingStepSkip({
        selfServeIsTeamAdmin: isAdmin,
        onboardingStep: step,
        featureLine: "onboarding",
      }),
    );
  };

  const logOnboardingStepContinue = (step: OnboardingStep | null) => {
    if (!step) return;
    reportPapEvent(
      PAP_Continue_DashSelfServeOnboarding({
        selfServeIsTeamAdmin: isAdmin,
        onboardingStep: step,
        featureLine: "onboarding",
      }),
    );
  };

  const handleButtonAction = (type: ActionButtonType) => {
    const onboardingStep = convertToOnboardingStep(currentStep?.step);
    switch (type) {
      case ActionButtonType.Skip:
        logOnboardingStepSkip(onboardingStep);
        handleContinue(true);
        break;
      case ActionButtonType.Later:
        logOnboardingStepSkip(onboardingStep);
        onOnboardingFinished();
        break;
      case ActionButtonType.Continue:
        logOnboardingStepContinue(onboardingStep);
        handleContinue();
        break;
      case ActionButtonType.Download:
        logOnboardingStepContinue(onboardingStep);
        handleDownload();
        break;
      default:
        return;
    }
  };

  const handleStepValidationChange = (valid: boolean) => {
    setContinueDisabled(!valid);
  };

  const getNavigatePathForStep = (step: SelfServeOnboardingStep): string => {
    const routePath = isAdminFlow
      ? RoutePath.SETUP_ADMIN_SELF_SERVE
      : RoutePath.SETUP_SELF_SERVE;
    return routePath.replace(":step", step);
  };

  const handleProvisionDash = async (isErrorPage?: boolean) => {
    const canProvisionDashFreemiumTeam = await canProvisionFreeDashTeam();
    if (canProvisionDashFreemiumTeam) {
      const success = await provisionFreeDashTeam(
        selfServeOnboardingState.companyName,
      );
      if (success) {
        // For founding admins of self-serve teams (the only users who will get to this step), we mark onboarding as complete as soon as the team is provisioned.
        // This is to ensure that the admin immediately receives a welcome email legal requirement at the moment of provisioning (legal requirement).
        // They have also already completed most of the onboarding steps, so we feel ok about marking them as complete at this point.
        await markDashUserDfbOnboardingCompleteIfNecessary(onboardingVariant);
      } else {
        if (!isErrorPage) {
          navigate(getNavigatePathForStep(SelfServeOnboardingStep.ERROR));
        }
        return false;
      }
    }

    // Refresh eligibility with retry logic
    // Run this even if the user is not eligible, to ensure the state is up-to-date
    const eligibility = await fetchUserEligibilityWithRetry();
    updateWithCheckResult(eligibility);
    return true;
  };

  const convertToOnboardingStep = (
    step: SelfServeOnboardingStep | undefined,
  ): OnboardingStep | null => {
    if (!step) return null;
    const stepMapping: Record<SelfServeOnboardingStep, OnboardingStep> = {
      [SelfServeOnboardingStep.COMPANY_INFO]: "name_and_logo",
      [SelfServeOnboardingStep.PROFILING]: "profiling",
      [SelfServeOnboardingStep.JTBD]: "jtbd",
      [SelfServeOnboardingStep.CONNECTORS]: "create_connectors",
      [SelfServeOnboardingStep.TEAM_INVITE]: "team_invite",
      [SelfServeOnboardingStep.DOWNLOAD_UPSELL]: "desktop_download",
      [SelfServeOnboardingStep.DOWNLOAD_EXTENSION_UPSELL]: "extension_download",
      [SelfServeOnboardingStep.LARGE_TEAM]: "large_team",
      [SelfServeOnboardingStep.ERROR]: "error",
    };

    return stepMapping[step] || null;
  };

  const hiddenNavigationSteps = [
    SelfServeOnboardingStep.LARGE_TEAM,
    SelfServeOnboardingStep.ERROR,
  ];

  const stepWidths: Partial<Record<SelfServeOnboardingStep, string>> = {
    [SelfServeOnboardingStep.JTBD]: "900px",
    [SelfServeOnboardingStep.TEAM_INVITE]: "600px",
    [SelfServeOnboardingStep.CONNECTORS]: "800px",
    [SelfServeOnboardingStep.DOWNLOAD_UPSELL]: "600px",
    [SelfServeOnboardingStep.DOWNLOAD_EXTENSION_UPSELL]: "600px",
    [SelfServeOnboardingStep.LARGE_TEAM]: "900px",
    [SelfServeOnboardingStep.ERROR]: "900px",
  };

  const footerContentItems: Partial<
    Record<SelfServeOnboardingStep, React.ReactNode>
  > = {
    [SelfServeOnboardingStep.CONNECTORS]: (
      <>
        <Text size="small" color="subtle" className={styles.legalNote}>
          {i18n.t("self_serve_connectors_legal_note_line_1")}
          <br />
          {i18n.t("self_serve_connectors_legal_note_line_2")}
        </Text>
      </>
    ),
  };

  const footerContent = currentStep?.step
    ? footerContentItems[currentStep.step]
    : undefined;

  const contentWidth = currentStep?.step
    ? stepWidths[currentStep.step]
    : undefined;

  if (!steps) return null;

  return (
    <OnboardingResponsesProvider {...selfServeOnboardingState}>
      <SelfServeOnboardingLayout
        title={currentStep?.title}
        description={currentStep?.description}
        width={contentWidth}
        footerContent={footerContent}
        displayNavigation={
          !hiddenNavigationSteps.includes(
            currentStep?.step ?? SelfServeOnboardingStep.COMPANY_INFO,
          )
        }
        actionButtons={
          <SelfServeOnboardingActions
            primaryActions={currentStep?.primaryActions ?? []}
            secondaryAction={currentStep?.secondaryAction}
            onButtonAction={handleButtonAction}
            continueDisabled={continueDisabled}
            continueLoading={continueLoading}
            skipValidation={currentStep?.skipValidation}
          />
        }
        totalSteps={steps?.filter((s) => !s.hidden).length}
        currentStep={currentStepIndex}
      >
        <Box display="flex" flexDirection="column" width={"100%"} flexGrow={1}>
          <SelfServeOnboardingItem
            nextStep={getNextStep()?.step}
            step={currentStep?.step}
            isAdmin={isAdmin}
            canProvisionDashFreemiumTeam={canProvisionDashFreemiumTeam}
            onValidationChange={handleStepValidationChange}
            onProvisionDash={handleProvisionDash}
            onContinue={() => handleContinue(true)}
            ref={onboardingStepRef}
          />
        </Box>
      </SelfServeOnboardingLayout>
    </OnboardingResponsesProvider>
  );
};

type SelfServeOnboardingActionsProps = {
  secondaryAction?: ActionButtonType;
  primaryActions: ActionButtonType[];
  onButtonAction: (type: ActionButtonType) => void;
  continueDisabled: boolean;
  continueLoading: boolean;
  skipValidation?: boolean;
};

const SelfServeOnboardingActions = ({
  secondaryAction,
  primaryActions,
  onButtonAction,
  continueDisabled,
  continueLoading,
  skipValidation = false,
}: SelfServeOnboardingActionsProps) => {
  const getButtonLabel = (type: ActionButtonType) => {
    switch (type) {
      case ActionButtonType.Continue:
        return i18n.t("continue_button");
      case ActionButtonType.Skip:
        return i18n.t("self_serve_skip");
      case ActionButtonType.Later:
        return i18n.t("self_serve_skip");
      case ActionButtonType.Download:
        return i18n.t("self_serve_download");
    }
  };

  const getButtonIconStart = (type: ActionButtonType) => {
    switch (type) {
      case ActionButtonType.Download:
        return <UIIcon src={DownloadLine} />;
      default:
        return undefined;
    }
  };

  const getButton = (type: ActionButtonType, disabled: boolean = false) => {
    const isContinueButton =
      type === ActionButtonType.Continue || type === ActionButtonType.Download;
    return (
      <Button
        className={styles.actionButton}
        key={`onboarding-button-${type}`}
        data-testid={`onboarding-button-${type}`}
        size="xlarge"
        variant={isContinueButton ? "primary" : "borderless"}
        onClick={() => onButtonAction(type)}
        isLoading={isContinueButton && continueLoading}
        disabled={isContinueButton && !skipValidation ? disabled : false}
        withIconStart={getButtonIconStart(type)}
      >
        <span>{getButtonLabel(type)}</span>
      </Button>
    );
  };

  const secondaryActionCtrl = secondaryAction
    ? getButton(secondaryAction, false)
    : null;

  return (
    <Box display="flex" flexGrow={1} width={"100%"} flexDirection="row">
      <div className={styles.actionButtonSpacers}>{secondaryActionCtrl}</div>
      <Box display="flex" flexDirection="row" className={styles.primaryActions}>
        {primaryActions.map((actionType) =>
          getButton(actionType, continueDisabled),
        )}
      </Box>
      <span className={styles.actionButtonSpacers}></span>
    </Box>
  );
};
